import React, { useState, useEffect } from 'react';
import {
    Backdrop,
    Card,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    ListItemText,
    OutlinedInput
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomFilter from 'src/components/filter/CustomFilter';
import Iconify from 'src/components/iconify';
import ClearButton from 'src/components/shared/ClearButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import FilterButton from 'src/components/shared/FilterButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import Services from 'src/services/Services';
import moment from 'moment';
import { toast } from 'react-toastify';

export default function ArasReportList({ parsedUser, tabs }) {
    const [reportList, setReportList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({});
    const isMobile = useMediaQuery('(max-width:768px)');
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState({});

    const TABLE_HEAD = [
        { id: 'inBranchDate', label: "Kargo Geliş Tarihi", format: (value) => formatDate(value) },
        { id: 'barcode', label: "Gönderi No" },
        { id: 'customerName', label: "Müşteri Ad Soyad" },
        { id: 'staff', label: "EP Personel Ad Soyadı" },
        { id: 'description', label: "Açıklama", format: (value) => value ? value : "-" },
        { id: 'processingType', label: "İşlem Türü" },
        { id: 'price', label: "Tutar", format: (value) => value ? value : "-" },
        { id: 'paymentType', label: "Ödeme Türü" },
        { id: 'processingDetails', label: "İşlem Detay" },
        { id: 'poiTitle', label: "Lokasyon" },
        {
            id: 'action',
            label: '',
            alignRight: false,
            format: (value, row) => (
                <Tooltip title={"Detay"}>
                    <Iconify
                        style={{ color: '#98A2B3', width: 22, height: 22, cursor: 'pointer' }}
                        icon="bx:detail"
                        onClick={() => handleOpenDetail(row)}
                    />
                </Tooltip>
            ),
        }
    ];

    const formatDate = (timestampOrMoment) => {
        if (moment.isMoment(timestampOrMoment)) return timestampOrMoment.format("DD.MM.YYYY");
        if (timestampOrMoment == null && timestampOrMoment !== 0) return "-";
        const date = new Date(timestampOrMoment * 1000);
        const y = date.getFullYear();
        if (isNaN(y) || y === 1970) return "-";
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${d}.${m}.${y}`;
    };

    // Çoklu seçim dropdownlar için benzersiz değer üretici
    const uniqueValues = (key) =>
        Array.from(new Set((originalData || []).map(x => x?.[key]).filter(Boolean)))
            .sort((a, b) => String(a).localeCompare(String(b), "tr-TR"));

    // === Tek yerde filtre uygulayan yardımcı ===
    const applyFilters = (data, currentFilters) => {
        if (!currentFilters || Object.keys(currentFilters).length === 0) return data;

        const asJsDate = (val, type = "start") => {
            if (!val) return null;
            if (moment.isMoment(val)) return (type === "start" ? val.startOf('day') : val.endOf('day')).toDate();
            if (val instanceof Date) return val;
            return new Date(val);
        };

        return (data || []).filter((item) => {
            return Object.entries(currentFilters).every(([key, filterValue]) => {
                // Tarih filtresi
                if (key === 'inBranchDate') {
                    const itemDate = item[key] ? new Date(item[key] * 1000) : null;
                    const start = asJsDate(filterValue?.start, 'start');
                    const end = asJsDate(filterValue?.end, 'end');
                    if ((start || end) && !itemDate) return false;
                    if (start && itemDate && itemDate < start) return false;
                    if (end && itemDate && itemDate > end) return false;
                    return true;
                }

                // Çoklu seçim (örn. poiTitle)
                if (Array.isArray(filterValue)) {
                    if (!filterValue.length) return true;
                    const raw = item[key];
                    const val = typeof raw === 'string' ? raw : String(raw ?? '');
                    return filterValue.map(String).includes(val);
                }

                // Metin arama
                if (!filterValue || (typeof filterValue === 'string' && filterValue.trim() === '')) return true;

                let itemValue = item[key];
                if (typeof itemValue === 'number') itemValue = String(itemValue);

                const columnDef = TABLE_HEAD.find(col => col.id === key);
                if (columnDef?.format) {
                    try { itemValue = columnDef.format(itemValue, item); } catch { /* noop */ }
                }

                return (
                    itemValue &&
                    String(itemValue).toLocaleUpperCase('tr-TR')
                        .includes(String(filterValue).toLocaleUpperCase('tr-TR'))
                );
            });
        });
    };

    useEffect(() => {
        getReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // === preserveFilters desteği ===
    const getReports = (preserveFilters = false) => {
        setLoading(true);
        Services.GetOperationReport(parsedUser.token, "aras-kargo")
            .then((res) => {
                if (res.data.status) {
                    const incoming = res.data.result || [];
                    setOriginalData(incoming);

                    const nextList =
                        preserveFilters && Object.keys(filters || {}).length > 0
                            ? applyFilters(incoming, filters)
                            : incoming;

                    setReportList(nextList);
                } else {
                    toast.error("Rapor bulunamadı.");
                    setReportList([]);
                    setOriginalData([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleModal = () => setFilterModal(true);
    const handleCloseModal = () => setFilterModal(false);

    const getFilteredArasReports = () => {
        const filteredData = applyFilters(originalData, filters);
        setReportList(filteredData);
        handleCloseModal();
    };

    const clearFilters = () => {
        setFilters({});
        setReportList(originalData);
    };

    const handleOpenDetail = (row) => {
        setSelectedRow(row);
        setFormData({
            ...row,
            inBranchDate: row.inBranchDate ? moment(row.inBranchDate * 1000) : null
        });
        setOpenDetail(true);
    };

    const handleCloseDetail = () => {
        setOpenDetail(false);
        setSelectedRow(null);
    };

    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const isDetailFormValid = () => {
        if (!formData) return false;
        const requiredFields = [
            'inBranchDate',
            'barcode',
            'customerName',
            'staff',
            'processingType',
            'paymentType',
            'processingDetails'
        ];
        for (let field of requiredFields) {
            if (!formData[field]) return false;
            if (typeof formData[field] === 'string' && formData[field].trim() === '') return false;
        }
        return true;
    };

    const handleSaveDetail = () => {
        const formattedData = {
            operationID: formData.operationID,
            inBranchDate: formData.inBranchDate ? formData.inBranchDate.format("YYYY-MM-DD") : null,
            barcode: formData.barcode,
            customerName: formData.customerName,
            staff: formData.staff,
            description: formData.description,
            processingType: formData.processingType,
            price: formData.price,
            paymentType: formData.paymentType,
            processingDetails: formData.processingDetails
        };

        setLoading(true);
        Services.UpdateOperationReport(parsedUser.token, [formattedData], "aras-kargo")
            .then((res) => {
                if (res.data.status) {
                    // Filtreleri koruyarak yeniden yükle
                    getReports(true);
                    handleCloseDetail();
                    toast.success("Rapor başarıyla güncellendi.");
                } else {
                    console.log(res.data);
                    toast.error("Rapor güncellenirken hata oluştu.");
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    return (
        <div>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                {tabs}
                {/* <div>
                    <ExportToExcelButton onClick={() => exportToExcel()} />
                </div> */}
            </Stack>

            <Stack direction="row" justifyContent={isMobile ? "space-between" : "end"} mb={5} paddingX={2}>
                {isMobile ? <FilterViewButton variant="contained" onClick={handleModal} /> : null}
            </Stack>

            <CustomFilter modalType="drawer" filterModal={filterModal} handleCloseModal={handleCloseModal}>
                <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" paddingY={2} paddingX={1}>
                    {TABLE_HEAD.map((column) => {
                        if (column.id === "inBranchDate") {
                            return (
                                <Stack key={column.id} direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label={`${column.label} Başlangıç`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.start || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), start: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                        <DatePicker
                                            label={`${column.label} Bitiş`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.end || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), end: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            );
                        }
                        // LOKASYON: çoklu seçim (checkbox'lu)
                        else if (column.id === "poiTitle") {
                            const options = uniqueValues(column.id);
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id={`${column.id}-label`}>{column.label}</InputLabel>
                                    <Select
                                        labelId={`${column.id}-label`}
                                        multiple
                                        input={<OutlinedInput label={column.label} />}
                                        renderValue={(selected) => (selected || []).join(", ")}
                                        value={filters[column.id] || []}
                                        onChange={(e) => {
                                            const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                                            setFilters({ ...filters, [column.id]: value });
                                        }}
                                    >
                                        {options.map(opt => (
                                            <MenuItem key={opt} value={opt}>
                                                <Checkbox checked={(filters[column.id] || []).indexOf(opt) > -1} />
                                                <ListItemText primary={opt} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        }
                        // AÇIKLAMA: input (metin)
                        else if (column.id === "description") {
                            return (
                                <TextField
                                    key={column.id}
                                    label={column.label}
                                    value={filters[column.id] || ''}
                                    onChange={(e) => setFilters({ ...filters, [column.id]: e.target.value })}
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                            );
                        }
                        // Diğer kolonlar default metin input
                        else if (column.label) {
                            return (
                                <TextField
                                    key={column.id}
                                    label={column.label}
                                    value={filters[column.id] || ''}
                                    onChange={(e) => setFilters({ ...filters, [column.id]: e.target.value })}
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                />
                            );
                        }
                        return null;
                    })}
                </Stack>

                <Divider sx={{ width: "100%", paddingTop: "10px" }} />
                <Stack mr={2} direction="row" alignItems="center" justifyContent="center" paddingY={2} paddingX={1}>
                    <ClearButton onClick={() => clearFilters()} />
                    <FilterButton onClick={() => getFilteredArasReports()} />
                </Stack>
            </CustomFilter>

            <Card>
                {loading ? (
                    <Backdrop sx={{ color: "#fff", zIndex: 2, height: "100%" }} open={loading}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                ) : (
                    <>
                        <DynamicTable
                            data={reportList}
                            headers={TABLE_HEAD}
                            isOrdered={true}
                            initialOrder="inBranchDate"
                            initalOrderDirection="desc"
                            isMobile={isMobile}
                            handleModal={handleModal}
                            handleCloseModal={handleCloseModal}
                        />
                        {/* Detay Modal */}
                        <Dialog open={openDetail} onClose={handleCloseDetail} fullWidth maxWidth="sm">
                            <DialogTitle>Detay Düzenle</DialogTitle>
                            <DialogContent>
                                <Stack spacing={2} mt={1}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Kargo Geliş Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.inBranchDate || null}
                                            onChange={(newValue) => handleFormChange('inBranchDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>

                                    <TextField
                                        label="Gönderi No"
                                        value={formData.barcode || ''}
                                        onChange={(e) => handleFormChange('barcode', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Müşteri Ad Soyad"
                                        value={formData.customerName || ''}
                                        onChange={(e) => handleFormChange('customerName', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />

                                    <TextField
                                        label="EP Personel Ad Soyadı"
                                        value={formData.staff || ''}
                                        onChange={(e) => handleFormChange('staff', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Açıklama"
                                        value={formData.description || ''}
                                        onChange={(e) => handleFormChange('description', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />

                                    <FormControl fullWidth>
                                        <InputLabel id="processingType-detail-label">İşlem Türü</InputLabel>
                                        <Select
                                            labelId="processingType-detail-label"
                                            value={formData.processingType || ''}
                                            label="İşlem Türü"
                                            onChange={(e) => handleFormChange('processingType', e.target.value)}
                                        >
                                            <MenuItem value=""><em>Seçiniz</em></MenuItem>
                                            {['İade', 'Satış'].map((option) => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label="Tutar"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        value={formData.price || ''}
                                        onChange={(e) => handleFormChange('price', e.target.value)}
                                        fullWidth
                                    />

                                    <FormControl fullWidth>
                                        <InputLabel id="paymentType-detail-label">Ödeme Türü</InputLabel>
                                        <Select
                                            labelId="paymentType-detail-label"
                                            value={formData.paymentType || ''}
                                            label="Ödeme Türü"
                                            onChange={(e) => handleFormChange('paymentType', e.target.value)}
                                        >
                                            <MenuItem value=""><em>Seçiniz</em></MenuItem>
                                            {["KREDİ KARTI", "NAKİT", "KARŞI ÖDEMELİ","İADE"].map((option) => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth>
                                        <InputLabel id="processingDetail-detail-label">İşlem Detay</InputLabel>
                                        <Select
                                            labelId="processingDetail-detail-label"
                                            value={formData.processingDetails || ''}
                                            label="İşlem Detay"
                                            onChange={(e) => handleFormChange('processingDetails', e.target.value)}
                                        >
                                            <MenuItem value=""><em>Seçiniz</em></MenuItem>
                                            {["ANLAŞMALI", "ÜCRETLİ GÖNDERİ", "İADE"].map((option) => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDetail}>İptal</Button>
                                <Button onClick={handleSaveDetail} variant="contained" disabled={!isDetailFormValid()}>
                                    Kaydet
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}
            </Card>
        </div>
    );
}